import { Nullable } from '@kitware/vtk.js/types';
import { AppViewViewIDs } from '@src/config';
import { useActiveStudyStore } from '@src/store/active-study';
import { SerializedSeriesClassificationSchema, seriesClassification } from '@src/store/dicom-web/SeriesClassification';
import { useUpdatedReportStore } from '@src/store/report-store';
import { StorageSerializers, useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';

import { computed, ref, watch } from 'vue';
import { z } from 'zod';

import { useAlert } from '@/src/composables/useAlert';
import { useAnalysisStore } from '@/src/store/analysis';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import { session, supabase } from '../supabase';
import { getMessage } from '../utils/exception';

import { AnalysisResultSchema } from './analysis/data-typing';
import { SerializedContourContainerSchema } from './contours/contour-container';
import { useContourContainerStore } from './contours/contour-container-store';
import { ImageIndicesSchema, useCurrentStudyStore } from './dicom-web/current-study-store';
import { SerializedStudyDataSchema } from './study-data-class';
import useCameraStore, { SerializedViewCameraStoreSchema } from './view-configs/camera';
import useViewSliceStore, { SerializedViewSliceStoreSchema } from './view-configs/slicing';
import useWindowingStore, { SerializedWindowingStoreSchema } from './view-configs/windowing';

export const ThemeModeSchema = z.enum(['dark', 'light']);
export type ThemeMode = z.infer<typeof ThemeModeSchema>;

const UserSettingsSchema = z.object({
  analysis: z.object({
    endoOffset: z.number(),
    epiOffset: z.number(),
  }),
  theme: z.object({
    mode: ThemeModeSchema,
  }),
  segmentation: z.record(z.string(), z.string()),
  patient: z.object({
    selectedKeys: z.array(z.string()),
  }),
  network: z.object({
    compression: z.boolean(),
  }),
});

export type UserSettings = z.infer<typeof UserSettingsSchema>;

const UserCardSchema = z.object({
  fn: z.string(),
  email: z.string().optional(),
  url: z.string().optional(),
});
export type UserCard = z.infer<typeof UserCardSchema>;

export const OrgCardSchema = z.object({
  fn: z.string(),
  url: z.string().optional(),
});
export type OrgCard = z.infer<typeof OrgCardSchema>;

export const SerializedAnalysisStateSchema = z.object({
  title: z.string(),
  createdBy: z.object({
    id: z.string(),
    card: UserCardSchema,
  }),
  createdAt: z.string(),
  appVersion: z.string(),
  studyInstanceUID: z.string(),
  contourContainerStore: z.record(z.string(), SerializedContourContainerSchema),
  studyData: SerializedStudyDataSchema,
  viewportIndexMap: z.record(z.string(), ImageIndicesSchema).optional(),
  windowing: SerializedWindowingStoreSchema.optional(),
  camera: SerializedViewCameraStoreSchema.optional(),
  viewSlice: SerializedViewSliceStoreSchema.optional(),
  classification: SerializedSeriesClassificationSchema.optional(),
  patientMeasurements: z.object({
    PatientWeight: z.number(),
    PatientSize: z.number(),
  }).nullable().optional(),
  analysisResult: AnalysisResultSchema.nullable(),
  report: z.unknown().optional(),
});
export type SerializedAnalysisState = z.infer<typeof SerializedAnalysisStateSchema>;

export const useAppState = defineStore('ocg-app-state', () => {
  const currentDayTime = ref();
  const currentBackGroundImage = ref();
  const analysisScreenDims = ref<Nullable<{ width: number; height: number }>>(null);
  const profileLoading = ref(false);
  const currentUser = useLocalStorage<UserCard>('currentUser', null, {
    serializer: StorageSerializers.object,
  }); // Current logged in user
  const currentUserName = computed(
    () =>
      currentUser.value?.fn
      ?? currentUser.value?.email
      ?? session.value?.user.email
      ?? '',
  );
  const currentThemeLocal = ref('dark');
  const currentTheme = computed(() => {
    return currentThemeLocal.value;
  });
  const currentUserOrgs = ref<{ id: string; card: OrgCard }[]>([]);

  // prettier-ignore
  const currentUserMode = ref/* <'user' | 'admin' | 'superadmin'> */('user');
  const appVersion = '1.0.0';
  const appVersionText = `OCG19 - Research Prototype V.${appVersion}`;

  const currentView = ref(useLocalStorage('currentView', 'QualityCheck'));
  const userActiveViewer = ref<string | null>(null); // The vtkviewer the user is currently interacting with (viewID)

  const setInteractionViewer = (viewID: string | null) => {
    userActiveViewer.value = viewID;
  };

  const getInteractionViewer = () => {
    return userActiveViewer.value;
  };

  const activeViewers = computed(() => {
    if (!currentView.value) {
      return [];
    }

    return AppViewViewIDs[currentView.value] ?? [];
  });
  /**
   * Gets the time of day based on the current hour.
   *
   * This function determines the time of day (Morning, Afternoon, Evening, or Day)
   * based on the current hour of the system clock. It is used for displaying a
   * personalized greeting and adjusting the user interface based on the time of day.
   *
   * @returns {string} The time of day as a string (Morning, Afternoon, Evening, or Day).
   */
  const timeOfDay = computed(() => {
    // Get the current hour from the system clock
    const currentHour = new Date().getHours();

    // Check the current hour and return the corresponding time of day
    if (currentHour >= 5 && currentHour < 12) {
      return 'Morning'; // If it's between 5 AM and 12 PM, it's morning
    }
    if (currentHour >= 12 && currentHour < 18) {
      return 'Afternoon'; // If it's between 12 PM and 6 PM, it's afternoon
    }
    if (currentHour >= 18 && currentHour < 23) {
      return 'Evening'; // If it's between 6 PM and 11 PM, it's evening
    }
    return 'Day'; // Otherwise, it's day (between 11 PM and 5 AM)
  });

  /**
   * Sets current logged in user.
   */
  function setUser(user: UserCard | null) {
    currentUser.value = user;
  }

  async function retrieveProfile() {
    if (!session.value) {
      setUser(null);
      return;
    }
    try {
      profileLoading.value = true;
      const { user } = session.value;
      // email.value = user.email ?? '';
      const { data, error, status } = await supabase
        .from('person')
        .select('card')
        .eq('id', user.id)
        .single();
      if (error && status !== 406) throw error;
      setUser(UserCardSchema.parse(data?.card));
    }
    catch (error) {
      if (error instanceof z.ZodError) {
        console.error(
          'Schema error: Cannot retrieve user profile (%s)',
          error.message,
        );
      }
      else {
        console.error(
          'Database error: Cannot retrieve user profile (%s)',
          getMessage(error),
        );
      }
      setUser(null);
    }
    try {
      const { data, error } = await supabase.rpc('user_mode');
      if (error) throw error;
      currentUserMode.value = data;
    }
    catch (error) {
      console.error(
        'Database error: Cannot retrieve user mode (%s)',
        getMessage(error),
      );
    }
    try {
      const { data, error } = await supabase
        .from('org')
        .select('id, person(card)')
        .order('person(card->fn)');
      if (error) throw error;
      currentUserOrgs.value = data.map(org => ({
        id: org.id,
        card: OrgCardSchema.parse(org.person.card),
      }));
    }
    catch (error) {
      console.error(
        'Database error: Cannot retrieve user organizations (%s)',
        getMessage(error),
      );
      currentUserOrgs.value = [];
    }
    profileLoading.value = false;
  }

  watch(
    () => session.value?.user.id ?? '',
    async () => retrieveProfile(),
    { immediate: true },
  );

  function setView(view: string) {
    currentView.value = view;
  }

  function setAnalysisScreenDims(width: number, height: number) {
    analysisScreenDims.value = { width, height };
  }

  async function logOut() {
    await supabase.auth.signOut();
    currentDayTime.value = null;
    currentBackGroundImage.value = null;
    setUser(null);
    currentView.value = 'QualityCheck';
  }

  function serializeAnalysisState(title: string) {
    const currentStudyStore = useCurrentStudyStore();
    const contourContainerStore = useContourContainerStore();
    const activeStudyStore = useActiveStudyStore();
    const viewportStateStore = useViewportStateStore();
    const windowingStore = useWindowingStore();
    const cameraStore = useCameraStore();
    const viewSliceStore = useViewSliceStore();
    const analysisStore = useAnalysisStore();
    const classification = seriesClassification.serialize();

    const biomarkerContainer = analysisStore.getBiomarkerContainer();
    const hasAnalysisResults
      = biomarkerContainer && biomarkerContainer.isStudyComplete();

    const reportStore = useUpdatedReportStore();
    let report: unknown = null;
    try {
      const reportJson = reportStore.serialize();
      if (reportJson) {
        report = JSON.parse(reportJson);
      }
    }
    catch (e) {
      console.error('Failed to serialize report during save:', e);
    }

    const serializedState = {
      title,
      createdBy: {
        id: session.value?.user.id,
        card: currentUser.value,
      },
      createdAt: new Date().toISOString(),
      appVersion,
      studyInstanceUID: currentStudyStore.getStudyID,
      contourContainerStore: contourContainerStore.serialize(),
      studyData: activeStudyStore.serializeStudyData(),
      viewportIndexMap: viewportStateStore.serializeSelectedImageIndices(),
      windowing: windowingStore.serialize(),
      camera: cameraStore.serialize(),
      viewSlice: viewSliceStore.serialize(),
      patientMeasurements: currentStudyStore.serializePatientMeasurements(),
      classification,
      analysisResult: hasAnalysisResults
        ? biomarkerContainer.serialize()
        : null,
      report,
    };

    return serializedState;
  }

  function loadAnalysisState(serializedState: SerializedAnalysisState) {
    const currentStudyStore = useCurrentStudyStore();
    const contourContainerStore = useContourContainerStore();
    const activeStudyStore = useActiveStudyStore();
    const analysisStore = useAnalysisStore();
    const viewportStateStore = useViewportStateStore();
    const windowingStore = useWindowingStore();
    const cameraStore = useCameraStore();
    const viewSliceStore = useViewSliceStore();

    const alert = useAlert();

    if (!serializedState) {
      return;
    }

    if (serializedState.studyInstanceUID !== currentStudyStore.getStudyID) {
      alert.error(
        'Serialized state studyInstanceUID does not match current study ID',
      );
      return;
    }
    contourContainerStore.deserialize(serializedState.contourContainerStore);
    activeStudyStore.deserializeStudyData(serializedState.studyData);
    if (serializedState.viewportIndexMap) {
      viewportStateStore.deserializeSelectedImageIndices(
        serializedState.viewportIndexMap,
      );
    }
    windowingStore.deserialize(serializedState.windowing);
    cameraStore.deserialize(serializedState.camera);
    viewSliceStore.deserialize(serializedState.viewSlice);
    currentStudyStore.deserializePatientMeasurements(
      serializedState.patientMeasurements ?? null,
    );

    // Check if classification data exists before deserializing, as older saves may not have this field
    if (serializedState.classification) {
      seriesClassification.deserialize(serializedState.classification);
    }
    // Here we need to check whether a viewportmap exists, if not we trigger seriesClassification to use default
    if (serializedState.viewportIndexMap) {
      viewportStateStore.deserializeSelectedImageIndices(
        serializedState.viewportIndexMap,
      );
    }
    else {
      // Apply default viewport map handling
      seriesClassification.applyDefaultViewportMapping();
    }
    const biomarkerContainer = analysisStore.resetStudy();
    if (serializedState.analysisResult) {
      biomarkerContainer.deserialize(serializedState.analysisResult);

      const analysisViewers = ['AnalysisLeft', 'AnalysisRight'];
      const QAViewers = ['ContoursLeft', 'ContoursRight'];
      const dynamicMaskTypes
        = biomarkerContainer.getMaskTypesBasedOnBiomarkerType('Dynamic');
      const staticMaskTypes
        = biomarkerContainer.getMaskTypesBasedOnBiomarkerType('Static');
      const polarMapTypes
        = biomarkerContainer.getMaskTypesBasedOnBiomarkerType('PolarMap');

      // Map each analysis viewer back to the confirmed selection.
      // When duplicated, both analysis viewers get the same indices
      // (from the single processed selection).
      for (let i = 0; i < analysisViewers.length; i++) {
        const viewID = analysisViewers[i];
        const sourceViewerID = QAViewers[i];

        const sourceIndices
          = viewportStateStore.getSelectedImageIndices(sourceViewerID);

        viewportStateStore.setSelectedImageIndices(viewID, sourceIndices, true);

        if (staticMaskTypes[0]) {
          viewportStateStore.setSelectedMarker(
            viewID,
            'Static',
            staticMaskTypes[0],
          );
        }

        if (dynamicMaskTypes[0]) {
          viewportStateStore.setSelectedMarker(
            viewID,
            'Dynamic',
            dynamicMaskTypes[0],
          );
        }
        if (polarMapTypes[0]) {
          viewportStateStore.setSelectedMarker(
            viewID,
            'PolarMap',
            polarMapTypes[0],
          );
        }
        viewportStateStore.setShowContours(viewID, false);
      }
    }

    const reportStore = useUpdatedReportStore();
    if (serializedState.report) {
      try {
        const reportData: string
          = typeof serializedState.report === 'string'
            ? serializedState.report
            : JSON.stringify(serializedState.report);
        reportStore.deserialize(reportData);
      }
      catch (e) {
        console.error('Failed to load serialized report:', e);
      }
    }
    else {
      reportStore.resetStore();
    }
  }

  function toggleColorTheme() {
    const htmlElement = document.documentElement;
    const dataTheme = htmlElement.getAttribute('data-theme');
    const preference = window.matchMedia(
      '(prefers-color-scheme: dark)',
    ).matches;

    // If no theme is set, use the opposite of system preference
    if (!dataTheme) {
      htmlElement.setAttribute('data-theme', preference ? 'light' : 'dark');
    }
    else {
      // Toggle between light and dark
      const newTheme = dataTheme === 'light' ? 'dark' : 'light';
      htmlElement.setAttribute('data-theme', newTheme);
      currentThemeLocal.value = newTheme;
    }
  }

  function setColorScheme(theme: ThemeMode) {
    const htmlElement = document.documentElement;
    htmlElement.setAttribute('data-theme', theme);
    currentThemeLocal.value = theme;
  }

  function setDefaultColorTheme() {
    const htmlElement = document.documentElement;
    const preference = window.matchMedia(
      '(prefers-color-scheme: dark)',
    ).matches;

    htmlElement.setAttribute('data-theme', preference ? 'dark' : 'light');
    currentThemeLocal.value = preference ? 'dark' : 'light';
  }

  async function retrieveUserSettings(): Promise<Partial<UserSettings> | null> {
    if (!session.value) {
      return null;
    }
    try {
      const { user } = session.value;
      const { data, error } = await supabase
        .from('profile')
        .select('user_app_settings')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return UserSettingsSchema.partial().parse(data?.user_app_settings) || null;
    }
    catch (error) {
      console.error(
        'Database error: Cannot retrieve user settings (%s)',
        getMessage(error),
      );
      return null;
    }
  }

  async function updateUserAppSettings(json: UserSettings) {
    if (!session.value) {
      return null;
    }
    try {
      const { user } = session.value;
      const { data, error } = await supabase
        .from('profile')
        .update({ user_app_settings: json })
        .eq('id', user.id)
        .select();

      if (error) throw error;
      return data;
    }
    catch (error) {
      console.error(
        'Database error: Cannot update user app settings (%s)',
        getMessage(error),
      );
      return null;
    }
  }

  function getCurrentLogo(forceLight = false) {
    // Return the logo path based on the current theme
    // Need to reverse the logic here
    // because the logo is dark in light mode and light in dark mode
    if (forceLight) {
      return '/logos/a19_master_light.svg';
    }
    return currentTheme.value === 'light'
      ? '/logos/a19_master_dark.svg'
      : '/logos/a19_master_light.svg';
  }

  return {
    currentDayTime,
    currentBackGroundImage,
    analysisScreenDims,
    currentUser,
    currentUserName,
    currentUserMode,
    currentUserOrgs,
    currentView,
    userActiveViewer,
    setInteractionViewer,
    getInteractionViewer,
    activeViewers,
    timeOfDay,
    session,
    appVersionText,
    setUser,
    setView,
    setAnalysisScreenDims,
    logOut,
    retrieveProfile,
    serializeAnalysisState,
    loadAnalysisState,
    toggleColorTheme,
    setDefaultColorTheme,
    setColorScheme,
    currentTheme,
    getCurrentLogo,
    retrieveUserSettings,
    updateUserAppSettings,
  };
});
